import { useRef, useEffect } from 'react';

const StarfieldCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let mouse = { x: -1000, y: -1000 };

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const handleMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };

    window.addEventListener('resize', resizeCanvas);
    window.addEventListener('mousemove', handleMouseMove);
    resizeCanvas();

    // 1. Define Constants and Colors First
    const colors = ["#FFFFFF", "#DAA520"];
    const starSizes = { small: 1, medium: 2, large: 3 };
    const nebulaColors = [
      { r: 75, g: 0, b: 130 },   // Indigo
      { r: 0, g: 0, b: 139 },    // Dark Blue
      { r: 139, g: 0, b: 139 },  // Dark Magenta
      { r: 25, g: 25, b: 112 },  // Midnight Blue
      { r: 48, g: 25, b: 52 },   // Deep Purple
      { r: 220, g: 20, b: 60 },  // Crimson (Rubin)
      { r: 255, g: 140, b: 0 },  // Dark Orange
      { r: 147, g: 112, b: 219 } // Medium Purple
    ];

    // 2. Define Shared Functions
    const drawDiamond = (x, y, size, color, opacity) => {
      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.beginPath();
      ctx.translate(x, y);
      ctx.rotate(45 * Math.PI / 180);
      ctx.fillStyle = color;
      ctx.fillRect(-size / 2, -size / 2, size, size);
      ctx.restore();
    };

    const createNebula = () => {
      const blobs = [];
      // Reduced number of blobs for better performance while keeping complex look
      const numBlobs = Math.floor(Math.random() * 5 + 4);
      const colorIndex = Math.floor(Math.random() * nebulaColors.length);
      const nextColorIndex = (colorIndex + 1) % nebulaColors.length;
      
      for (let j = 0; j < numBlobs; j++) {
        blobs.push({
          offsetX: (Math.random() - 0.5) * 500,
          offsetY: (Math.random() - 0.5) * 500,
          radius: Math.random() * 400 + 350,
          phase: Math.random() * Math.PI * 2,
          phaseSpeed: Math.random() * 0.01 + 0.005,
          sizeAmplitude: Math.random() * 80 + 30
        });
      }

      // Add stars inside nebula
      const nebulaStars = [];
      const numNebulaStars = Math.floor(Math.random() * 10 + 8);
      for (let i = 0; i < numNebulaStars; i++) {
        nebulaStars.push({
          relX: (Math.random() - 0.5) * 700,
          relY: (Math.random() - 0.5) * 700,
          size: Math.random() * 2 + 0.5,
          opacity: Math.random() * 0.8 + 0.2,
          blinkSpeed: Math.random() * 0.03 + 0.01
        });
      }

      return {
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        // Increased velocity for visible movement in various directions
        vx: (Math.random() - 0.5) * 1.2 + (Math.random() > 0.5 ? 0.4 : -0.4),
        vy: (Math.random() - 0.5) * 1.2 + (Math.random() > 0.5 ? 0.4 : -0.4),
        blobs,
        stars: nebulaStars,
        colorIndex,
        nextColorIndex,
        colorTransition: 0,
        colorSpeed: Math.random() * 0.001 + 0.0005,
        life: 0,
        maxLife: Math.random() * 0.6 + 0.4,
        decay: 0.0001 + Math.random() * 0.0001,
        state: 'fadeIn'
      };
    };

    const createGalaxy = (spawnOffScreen = false, targetGalaxy = null) => {
      let x, y;
      const margin = 300;
      
      if (spawnOffScreen) {
        const side = Math.floor(Math.random() * 4);
        if (side === 0) { x = Math.random() * canvas.width; y = -margin; }
        else if (side === 1) { x = Math.random() * canvas.width; y = canvas.height + margin; }
        else if (side === 2) { x = -margin; y = Math.random() * canvas.height; }
        else { x = canvas.width + margin; y = Math.random() * canvas.height; }
      } else {
        x = Math.random() * canvas.width;
        y = Math.random() * canvas.height;
      }

      const numGalaxyStars = Math.floor(Math.random() * 80 + 100);
      const galaxyStars = [];
      const color = nebulaColors[Math.floor(Math.random() * nebulaColors.length)];
      
      for (let i = 0; i < numGalaxyStars; i++) {
        const arm = Math.floor(Math.random() * 2);
        const angle = (Math.random() * 2 + arm * Math.PI);
        const dist = Math.random() * 150 + 20;
        galaxyStars.push({
          initialAngle: angle,
          dist,
          size: Math.random() * 2.5 + 0.5,
          opacity: Math.random() * 0.7 + 0.3,
          blinkSpeed: Math.random() * 0.05 + 0.01
        });
      }

      let vx, vy;
      const galaxyId = Math.random().toString(36).substr(2, 9);
      let targetId = null;

      if (spawnOffScreen && targetGalaxy && Math.random() < 0.4) { // Only 40% chance to target a "friend"
        const dx = targetGalaxy.x - x;
        const dy = targetGalaxy.y - y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const speed = Math.random() * 0.4 + 0.6;
        const offsetX = (dy / dist) * (Math.random() > 0.5 ? 1 : -1) * 0.8;
        const offsetY = (-dx / dist) * (Math.random() > 0.5 ? 1 : -1) * 0.8;
        vx = (dx / dist) * speed + offsetX;
        vy = (dy / dist) * speed + offsetY;
        targetId = targetGalaxy.id;
      } else {
        vx = (Math.random() - 0.5) * 0.8;
        vy = (Math.random() - 0.5) * 0.8;
        if (spawnOffScreen) {
          const dx = (canvas.width / 2) - x;
          const dy = (canvas.height / 2) - y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          vx = (dx / dist) * (Math.random() * 0.6 + 0.4);
          vy = (dy / dist) * (Math.random() * 0.6 + 0.4);
        }
      }

      return {
        x, y, vx, vy,
        stars: galaxyStars,
        color: `rgba(${color.r}, ${color.g}, ${color.b}, 0.15)`,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() * 0.001 + 0.001) * (Math.random() > 0.5 ? 1 : -1),
        tilt: Math.random() * 0.5 + 0.3,
        coreRadius: Math.random() * 12 + 8,
        coreRotation: 0,
        coreRotationSpeed: (Math.random() * 0.01 + 0.005),
        mass: Math.random() * 5000 + 3000,
        id: galaxyId,
        targetId: targetId,
        fusionProgress: 0, // 0 to 1
        isDancing: false,
        danceInitiated: false,
        orbitAngle: 0,
        orbitRadius: 0,
        initialOrbitAngle: 0,
        completedOrbits: 0
      };
    };

    const createComet = () => {
      const side = Math.floor(Math.random() * 4);
      let x, y, angle;
      if (side === 0) { x = Math.random() * canvas.width; y = -20; angle = Math.PI / 4 + Math.random() * Math.PI / 2; }
      else if (side === 1) { x = canvas.width + 20; y = Math.random() * canvas.height; angle = Math.PI * 0.75 + Math.random() * Math.PI / 2; }
      else if (side === 2) { x = Math.random() * canvas.width; y = canvas.height + 20; angle = Math.PI * 1.25 + Math.random() * Math.PI / 2; }
      else { x = -20; y = Math.random() * canvas.height; angle = -Math.PI / 4 + Math.random() * Math.PI / 2; }
      const speed = Math.random() * 2 + 1.5;
      return { x, y, size: starSizes.large * 3, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, life: 1.0, decay: 0.004 + Math.random() * 0.006, trail: [], curve: (Math.random() - 0.5) * 0.015 };
    };

    // 3. Initialize Entities
    const stars = [];
    const numStars = 150;
    for (let i = 0; i < numStars; i++) {
      const sizeCategory = ['small', 'medium', 'large'][Math.floor(Math.random() * 3)];
      const initialSize = starSizes[sizeCategory] + Math.random() * 0.5 - 0.25;
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: initialSize,
        maxSize: initialSize + Math.random() * 1.0,
        vx: (Math.random() - 0.5) * 0.1,
        vy: (Math.random() - 0.5) * 0.1,
        color: colors[Math.floor(Math.random() * colors.length)],
        opacity: Math.random() * 0.7,
        pulsateSpeed: Math.random() * 0.01 + 0.0025,
        pulsateDirection: Math.random() < 0.5 ? 1 : -1,
      });
    }

    const nebulae = [];
    for (let i = 0; i < 6; i++) {
      nebulae.push(createNebula());
    }

    const galaxies = [];
    for (let i = 0; i < 7; i++) galaxies.push(createGalaxy());

    const comets = [];
    let lastCometTime = 0;

    // 4. Animation Loop
    const animate = (time) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // --- Physics Step: Gravitational Interactions ---
      const G = 0.06; // Slightly reduced for smoother motion
      for (let i = 0; i < galaxies.length; i++) {
        for (let j = i + 1; j < galaxies.length; j++) {
          const g1 = galaxies[i];
          const g2 = galaxies[j];
          
          const dx = g2.x - g1.x;
          const dy = g2.y - g1.y;
          const distSq = dx * dx + dy * dy;
          const dist = Math.sqrt(distSq);
          
          const isFriend = g1.targetId === g2.id || g2.targetId === g1.id;
          
          // --- ENHANCED ORBITAL CAPTURE LOGIC ---
          // Reverted to 350px range and friends-only dance
          if (dist < 350 && isFriend) {
            g1.isDancing = true;
            g2.isDancing = true;
            
            const totalMass = g1.mass + g2.mass;
            const centerX = (g1.x * g1.mass + g2.x * g2.mass) / totalMass;
            const centerY = (g1.y * g1.mass + g2.y * g2.mass) / totalMass;

            if (!g1.danceInitiated) {
              const startAngle1 = Math.atan2(g1.y - centerY, g1.x - centerX);
              const startAngle2 = Math.atan2(g2.y - centerY, g2.x - centerX);
              g1.orbitAngle = startAngle1;
              g2.orbitAngle = startAngle2;
              g1.initialOrbitAngle = startAngle1;
              g2.initialOrbitAngle = startAngle2;
              
              // Force initial radius to prevent immediate fusion
              const currentRad1 = Math.sqrt((g1.x - centerX)**2 + (g1.y - centerY)**2);
              const currentRad2 = Math.sqrt((g2.x - centerX)**2 + (g2.y - centerY)**2);
              g1.orbitRadius = Math.max(150, currentRad1);
              g2.orbitRadius = Math.max(150, currentRad2);
              
              // BRAKE: Drastically reduce linear velocity on capture
              g1.vx *= 0.2; g1.vy *= 0.2;
              g2.vx *= 0.2; g2.vy *= 0.2;

              g1.danceInitiated = true;
              g2.danceInitiated = true;
              g1.completedOrbits = 0;
            }

            // Calculate completed orbits
            const currentRelAngle = Math.abs(g1.orbitAngle - g1.initialOrbitAngle);
            g1.completedOrbits = currentRelAngle / (Math.PI * 2);

            // Moderate progress
            const progressSpeed = g1.completedOrbits < 2.5 ? 0.001 : 0.008;
            g1.fusionProgress = Math.min(1, g1.fusionProgress + progressSpeed);
            g2.fusionProgress = g1.fusionProgress;
            
            // Spiral inward
            let spiralFactor;
            if (g1.completedOrbits < 2) {
              spiralFactor = 1 - (g1.completedOrbits / 2) * 0.4;
            } else if (g1.completedOrbits < 3) {
              spiralFactor = 0.6 * (1 - (g1.completedOrbits - 2)); 
            } else {
              spiralFactor = 0; 
            }
              
            const targetRadius1 = 150 * spiralFactor * (g1.mass / totalMass * 2);
            const targetRadius2 = 150 * spiralFactor * (g2.mass / totalMass * 2);
            
            // Kepler Speed
            const baseSpeed = 0.012 + (g1.completedOrbits * 0.015);
            g1.orbitAngle += baseSpeed;
            g2.orbitAngle += baseSpeed;
            
            // Move orbitRadius towards target
            const radiusLerp = g1.completedOrbits < 2 ? 0.04 : 0.15;
            g1.orbitRadius += (targetRadius1 - g1.orbitRadius) * radiusLerp;
            g2.orbitRadius += (targetRadius2 - g2.orbitRadius) * radiusLerp;
            
            const targetX1 = centerX + Math.cos(g1.orbitAngle) * g1.orbitRadius;
            const targetY1 = centerY + Math.sin(g1.orbitAngle) * g1.orbitRadius;
            const targetX2 = centerX + Math.cos(g2.orbitAngle) * g2.orbitRadius;
            const targetY2 = centerY + Math.sin(g2.orbitAngle) * g2.orbitRadius;
            
            // Force positions to target orbital path
            const followStrength = g1.completedOrbits < 2 ? 0.3 : 0.6;
            g1.vx = (targetX1 - g1.x) * followStrength + g1.vx * 0.3;
            g1.vy = (targetY1 - g1.y) * followStrength + g1.vy * 0.3;
            g2.vx = (targetX2 - g2.x) * followStrength + g2.vx * 0.3;
            g2.vy = (targetY2 - g2.y) * followStrength + g2.vy * 0.3;
 
             // Apply friction
             g1.vx *= 0.97; g1.vy *= 0.97;
             g2.vx *= 0.97; g2.vy *= 0.97;
 
              // Merging logic: TRIGGER FUSION EXACTLY AT 3 ORBITS
              if (g1.completedOrbits >= 3.0) { 
                g1.x = centerX;
                g1.y = centerY;
                g1.vx = (g1.vx * g1.mass + g2.vx * g2.mass) / totalMass;
                g1.vy = (g1.vy * g1.mass + g2.vy * g2.mass) / totalMass;
                g1.mass = totalMass;
                g1.coreRadius = Math.sqrt(g1.coreRadius * g1.coreRadius + g2.coreRadius * g2.coreRadius);
                g1.stars.push(...g2.stars);
                g1.fusionProgress = 0;
                g1.isDancing = false;
                g1.danceInitiated = false;
                g1.completedOrbits = 0;
                galaxies.splice(j, 1);
                const otherTarget = galaxies.length > 0 ? galaxies[Math.floor(Math.random() * galaxies.length)] : null;
                galaxies.push(createGalaxy(true, otherTarget));
                continue;
              }
            }
          
          // Apply gravity
          const currentG = isFriend ? G * 1.5 : G * 0.1;
          const pullForce = (currentG * g1.mass * g2.mass) / (distSq + 1000); 
          
          const ax = (pullForce * dx) / dist;
          const ay = (pullForce * dy) / dist;
          
          if (!g1.isDancing) {
            g1.vx += ax / g1.mass;
            g1.vy += ay / g1.mass;
          }
          if (!g2.isDancing) {
            g2.vx -= ax / g2.mass;
            g2.vy -= ay / g2.mass;
          }
          
          // Orbital stabilization: only for friends
          const correctionStrength = (isFriend && dist > 200 && dist < 800) ? 0.008 : 0; 
          
          if (correctionStrength > 0) {
            g1.vx += (dx / dist) * correctionStrength;
            g1.vy += (dy / dist) * correctionStrength;
            g2.vx -= (dx / dist) * correctionStrength;
            g2.vy -= (dy / dist) * correctionStrength;
          }

          // Star exchange logic (if a star is closer to the other galaxy, it might get pulled away)
          if (dist < 450) {
            const exchangeChance = 0.05; // 5% chance per frame to check for star exchange
            
            // Check g1 stars
            if (Math.random() < exchangeChance) {
              for (let k = g1.stars.length - 1; k >= 0; k--) {
                const s = g1.stars[k];
                const sAbsX = g1.x + Math.cos(s.initialAngle + g1.rotation) * s.dist;
                const sAbsY = g1.y + Math.sin(s.initialAngle + g1.rotation) * s.dist;
                
                const dx2 = g2.x - sAbsX;
                const dy2 = g2.y - sAbsY;
                const distToG2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
                
                if (distToG2 < s.dist * 0.7) { 
                  const newAngle = Math.atan2(sAbsY - g2.y, sAbsX - g2.x) - g2.rotation;
                  s.initialAngle = newAngle;
                  s.dist = distToG2;
                  g2.stars.push(g1.stars.splice(k, 1)[0]);
                  break; // Only one star per frame for performance
                }
              }
            }
            // Check g2 stars
            if (Math.random() < exchangeChance) {
              for (let k = g2.stars.length - 1; k >= 0; k--) {
                const s = g2.stars[k];
                const sAbsX = g2.x + Math.cos(s.initialAngle + g2.rotation) * s.dist;
                const sAbsY = g2.y + Math.sin(s.initialAngle + g2.rotation) * s.dist;
                
                const dx1 = g1.x - sAbsX;
                const dy1 = g1.y - sAbsY;
                const distToG1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
                
                if (distToG1 < s.dist * 0.7) {
                  const newAngle = Math.atan2(sAbsY - g1.y, sAbsX - g1.x) - g1.rotation;
                  s.initialAngle = newAngle;
                  s.dist = distToG1;
                  g1.stars.push(g2.stars.splice(k, 1)[0]);
                  break;
                }
              }
            }
          }
        }
      }
      // --- End Physics Step ---

      // Global Composite Operation for smoother nebula blending
      ctx.globalCompositeOperation = 'screen';

      // Draw Nebulae
      for (let i = nebulae.length - 1; i >= 0; i--) {
        const n = nebulae[i];
        
        // Handle Lifecycle and Color Transition
        if (n.state === 'fadeIn') {
          n.life += 0.002;
          if (n.life >= n.maxLife) n.state = 'stable';
        } else if (n.state === 'fadeOut') {
          n.life -= n.decay * 2;
          if (n.life <= 0) {
            nebulae.splice(i, 1);
            nebulae.push(createNebula());
            continue;
          }
        } else {
          n.life -= n.decay;
          if (n.life < 0.1) n.state = 'fadeOut';
        }

        n.colorTransition += n.colorSpeed;
        if (n.colorTransition >= 1) {
          n.colorIndex = n.nextColorIndex;
          n.nextColorIndex = (n.colorIndex + 1) % nebulaColors.length;
          n.colorTransition = 0;
        }

        const c1 = nebulaColors[n.colorIndex];
        const c2 = nebulaColors[n.nextColorIndex];
        const r = Math.floor(c1.r + (c2.r - c1.r) * n.colorTransition);
        const g = Math.floor(c1.g + (c2.g - c1.g) * n.colorTransition);
        const b = Math.floor(c1.b + (c2.b - c1.b) * n.colorTransition);
        // Increased alpha multiplier from 0.05 to 0.12 for more vibrant colors
        const currentColor = `rgba(${r}, ${g}, ${b}, ${n.life * 0.12})`;

        n.blobs.forEach(blob => {
          blob.phase += blob.phaseSpeed;
          const currentRadius = blob.radius + Math.sin(blob.phase) * blob.sizeAmplitude;
          
          const grad = ctx.createRadialGradient(
            n.x + blob.offsetX, n.y + blob.offsetY, 0,
            n.x + blob.offsetX, n.y + blob.offsetY, currentRadius
          );
          grad.addColorStop(0, currentColor);
          grad.addColorStop(1, "transparent");
          
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(n.x + blob.offsetX, n.y + blob.offsetY, currentRadius, 0, Math.PI * 2);
          ctx.fill();
        });

        // Draw stars inside nebula
        n.stars.forEach(s => {
          s.opacity += (Math.random() - 0.5) * s.blinkSpeed;
          s.opacity = Math.max(0.1, Math.min(0.8, s.opacity));
          ctx.beginPath();
          ctx.arc(n.x + s.relX, n.y + s.relY, s.size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${s.opacity * (n.life / n.maxLife)})`;
          ctx.fill();
        });

        n.x += n.vx; n.y += n.vy;
        if (n.x < -800 || n.x > canvas.width + 800) n.vx *= -1;
        if (n.y < -800 || n.y > canvas.height + 800) n.vy *= -1;
      }

      ctx.globalCompositeOperation = 'source-over';

      // Draw Galaxies
      for (let i = galaxies.length - 1; i >= 0; i--) {
        const g = galaxies[i];
        g.x += g.vx; g.y += g.vy;
        g.rotation += g.rotationSpeed;
        g.coreRotation += g.coreRotationSpeed;
        
        // Reset dancing flag at start of each frame - will be set in physics step if still close to friend
        g.isDancing = false;
        
        // Remove galaxies when they go far off-screen and spawn new ones
        const margin = 500;
        if (g.x < -margin || g.x > canvas.width + margin || g.y < -margin || g.y > canvas.height + margin) {
          galaxies.splice(i, 1);
          galaxies.push(createGalaxy(true)); // Spawn new galaxy off-screen to fly in
          continue;
        }
        
        ctx.save();
        ctx.translate(g.x, g.y);
        ctx.rotate(g.rotation);
        ctx.scale(1, g.tilt);
        
        // Draw Galaxy Nebula Background
        const galaxyGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, 200);
        galaxyGrad.addColorStop(0, g.color);
        galaxyGrad.addColorStop(1, 'transparent');
        ctx.fillStyle = galaxyGrad;
        ctx.beginPath();
        ctx.arc(0, 0, 200, 0, Math.PI * 2);
        ctx.fill();

        // Draw Luminous Blur / Accretion Disk Glow (Enhanced)
        const glowGrad = ctx.createRadialGradient(0, 0, g.coreRadius, 0, 0, g.coreRadius * 6);
        glowGrad.addColorStop(0, 'rgba(255, 255, 255, 0.3)');
        glowGrad.addColorStop(0.1, 'rgba(255, 200, 100, 0.2)');
        glowGrad.addColorStop(0.4, 'rgba(255, 100, 50, 0.1)');
        glowGrad.addColorStop(1, 'transparent');
        ctx.fillStyle = glowGrad;
        ctx.beginPath();
        ctx.arc(0, 0, g.coreRadius * 6, 0, Math.PI * 2);
        ctx.fill();

        // Draw Jagged Edges / Corona Rays
        ctx.save();
        ctx.rotate(g.coreRotation * 0.5);
        const numRays = 12;
        for (let i = 0; i < numRays; i++) {
          const angle = (i / numRays) * Math.PI * 2;
          const rayLen = g.coreRadius * (3 + Math.sin(g.coreRotation + i) * 1.5);
          const rayWidth = g.coreRadius * 1.2;
          
          const rayGrad = ctx.createRadialGradient(
            Math.cos(angle) * g.coreRadius, Math.sin(angle) * g.coreRadius, 0,
            Math.cos(angle) * g.coreRadius, Math.sin(angle) * g.coreRadius, rayLen
          );
          rayGrad.addColorStop(0, 'rgba(255, 255, 255, 0.2)');
          rayGrad.addColorStop(1, 'transparent');
          
          ctx.fillStyle = rayGrad;
          ctx.beginPath();
          ctx.ellipse(
            Math.cos(angle) * (g.coreRadius + rayLen / 2),
            Math.sin(angle) * (g.coreRadius + rayLen / 2),
            rayLen / 2, rayWidth / 2, angle, 0, Math.PI * 2
          );
          ctx.fill();
        }
        ctx.restore();

        // Draw Black Hole Core with Rotation Effect
        ctx.save();
        ctx.rotate(g.coreRotation);
        const coreGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, g.coreRadius * 1.5);
        coreGrad.addColorStop(0, 'black');
        coreGrad.addColorStop(0.5, 'black');
        coreGrad.addColorStop(0.7, 'rgba(255, 255, 255, 0.8)'); // Event horizon light
        coreGrad.addColorStop(1, 'transparent');
        ctx.fillStyle = coreGrad;
        ctx.beginPath();
        ctx.arc(0, 0, g.coreRadius * 1.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
  
        // Draw Spiral Arms Stars
        g.stars.forEach(s => {
          // Three-tier rotation speed system:
          // 1. Near center (dist < 50): High speed (as now)
          // 2. Middle (50 < dist < 100): ~2x slower
          // 3. Edge (dist > 100): Almost stationary
          
          let speedFactor;
          if (s.dist < 50) {
            speedFactor = 6.0; // High speed
          } else if (s.dist < 110) {
            speedFactor = 2.5; // ~2x slower than high
          } else {
            speedFactor = 0.4; // Almost stationary
          }
          
          // Boost star rotation during dance to feel the energy building up
          const danceBoost = g.isDancing ? (1 + g.fusionProgress * 1.5) : 1;
          const currentAngle = s.initialAngle + g.rotation * speedFactor * danceBoost;
          
          const x = Math.cos(currentAngle) * s.dist;
          const y = Math.sin(currentAngle) * s.dist;
          
          ctx.beginPath();
          ctx.arc(x, y, s.size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${s.opacity})`;
          ctx.fill();
        });
        ctx.restore();
      }

      // Draw Constellations
      ctx.beginPath();
      ctx.strokeStyle = "rgba(255, 255, 255, 0.02)";
      ctx.lineWidth = 0.5;
      for (let i = 0; i < stars.length; i++) {
        for (let j = i + 1; j < stars.length; j++) {
          const dx = stars[i].x - stars[j].x;
          const dy = stars[i].y - stars[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 80) {
            ctx.moveTo(stars[i].x, stars[i].y);
            ctx.lineTo(stars[j].x, stars[j].y);
          }
        }
      }
      ctx.stroke();

      // Draw Comets
      if (time - lastCometTime > 2500) {
        comets.push(createComet());
        lastCometTime = time;
      }
      for (let i = comets.length - 1; i >= 0; i--) {
        const c = comets[i];
        c.trail.push({ x: c.x, y: c.y });
        if (c.trail.length > 25) c.trail.shift();
        const speed = Math.sqrt(c.vx * c.vx + c.vy * c.vy);
        const newAngle = Math.atan2(c.vy, c.vx) + c.curve;
        c.vx = Math.cos(newAngle) * speed;
        c.vy = Math.sin(newAngle) * speed;
        c.x += c.vx; c.y += c.vy;
        c.life -= c.decay;
        if (c.life <= 0 || c.x < -150 || c.x > canvas.width + 150 || c.y < -150 || c.y > canvas.height + 150) {
          comets.splice(i, 1);
        } else {
          c.trail.forEach((p, idx) => {
            const ratio = idx / c.trail.length;
            ctx.beginPath();
            ctx.arc(p.x, p.y, (c.size / 2) * ratio, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255, 255, 255, ${c.life * ratio * 0.2})`;
            ctx.fill();
          });
          drawDiamond(c.x, c.y, c.size, "#FFFFFF", c.life);
        }
      }

      // Draw Stars
      stars.forEach(star => {
        star.x += star.vx; star.y += star.vy;
        const dx = mouse.x - star.x;
        const dy = mouse.y - star.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 120) {
          const force = (120 - dist) / 120;
          star.x -= dx * force * 0.015;
          star.y -= dy * force * 0.015;
        }
        star.opacity += star.pulsateSpeed * star.pulsateDirection;
        if (star.opacity <= 0 || star.opacity >= 1) star.pulsateDirection *= -1;
        star.opacity = Math.max(0, Math.min(1, star.opacity));
        star.size = star.maxSize * star.opacity;
        if (star.x < -star.size) star.x = canvas.width + star.size;
        if (star.x > canvas.width + star.size) star.x = -star.size;
        if (star.y < -star.size) star.y = canvas.height + star.size;
        if (star.y > canvas.height + star.size) star.y = -star.size;
        drawDiamond(star.x, star.y, star.size, star.color, star.opacity);
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate(0);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, zIndex: -1, background: '#000' }} />;
};

export default StarfieldCanvas;
